import { observable } from 'mobx';
import { isEmpty } from 'lodash';

export type HotelOrderItemAgodaTodoMapperResponseArgs = {
  reservation_number: string | null;
  people_num: number | null;
  hotel_name: string | null;
  hotel_address: string | null;
  room_type: string | null;
  plan_name: string | null;
  breakfast: boolean | null;
  checkin_date: string | null;
  checkout_date: string | null;
  traveler_name: string | null;
  status?: string | null;
  self_service_url?: string | null;
};

export type HotelOrderItemAgodaTodoResponseArgs = {
  from_order_item: HotelOrderItemAgodaTodoMapperResponseArgs;
  from_agoda_reservation: HotelOrderItemAgodaTodoMapperResponseArgs;
  diff: { [key: string]: boolean | string | number | null };
  price: number | null;
  original_price: number | null;
  agoda_price: number | null;
  agoda_price_unconvertible: boolean;
  supplied_item_name: string | null;
  is_inventoried: boolean;
  inventoried_by: string | null;
  inventoried_at: string | null;
  trip_id: number | null;
  order_item_id: number | null;
  trace_id: number | null;
  restricted: boolean | null;
  user_checker: boolean | null;
  last_log_message: string | null;
  last_log_message_arranger: string | null;
  original_plan_name: string | null;
  show_multiple_hotel_plans: boolean;
  special_checkin_instructions: string | null;
};

type HotelOrderItemAgodaTodoMapperArgs = {
  reservationNumber: string | null;
  peopleNum: number | null;
  hotelName: string | null;
  hotelAddress: string | null;
  roomType: string | null;
  planName: string | null;
  breakfast: boolean | null;
  checkinDate: string | null;
  checkoutDate: string | null;
  travelerName: string | null;
  status?: string | null;
  selfServiceUrl?: string | null;
};

type HotelOrderItemAgodaTodoArgs = {
  fromOrderItem: HotelOrderItemAgodaTodoMapperArgs;
  fromAgodaReservation: HotelOrderItemAgodaTodoMapperArgs;
  diff: { [key: string]: boolean };
  price: number | null;
  originalPrice: number | null;
  agodaPrice: number | null;
  agodaPriceUnconvertible: boolean;
  suppliedItemName: string | null;
  isInventoried: boolean;
  inventoriedBy: string | null;
  inventoriedAt: string | null;
  tripId: number | null;
  orderItemId: number | null;
  traceId: number | null;
  restricted: boolean | null;
  userChecker: boolean | null;
  lastLogMessage: string | null;
  lastLogMessageArranger: string | null;
  originalPlanName: string | null;
  showMultipleHotelPlans: boolean;
  specialCheckinInstructions: string | null;
};

const convertMapper = (
  response: HotelOrderItemAgodaTodoMapperResponseArgs
): HotelOrderItemAgodaTodoMapperArgs => ({
  reservationNumber: response.reservation_number,
  peopleNum: response.people_num,
  hotelName: response.hotel_name,
  hotelAddress: response.hotel_address,
  roomType: response.room_type,
  planName: response.plan_name,
  breakfast: response.breakfast,
  checkinDate: response.checkin_date,
  checkoutDate: response.checkout_date,
  travelerName: response.traveler_name,
  status: response.status,
  selfServiceUrl: response.self_service_url
});

const convertHotelOrderItemAgodaTodoResponseToArgs = (
  response: HotelOrderItemAgodaTodoResponseArgs
): HotelOrderItemAgodaTodoArgs => ({
  fromOrderItem: convertMapper(response.from_order_item),
  fromAgodaReservation: convertMapper(response.from_agoda_reservation),
  diff: Object.entries(response.diff || {}).reduce(
    (acc, [key, value]) => ({ ...acc, [key]: value === true || !isEmpty(value) }),
    {} as { [key: string]: boolean }
  ),
  price: response.price,
  originalPrice: response.original_price,
  agodaPrice: response.agoda_price,
  agodaPriceUnconvertible: response.agoda_price_unconvertible,
  suppliedItemName: response.supplied_item_name,
  isInventoried: response.is_inventoried,
  inventoriedBy: response.inventoried_by,
  inventoriedAt: response.inventoried_at,
  tripId: response.trip_id,
  orderItemId: response.order_item_id,
  traceId: response.trace_id,
  restricted: response.restricted,
  userChecker: response.user_checker,
  lastLogMessage: response.last_log_message,
  lastLogMessageArranger: response.last_log_message_arranger,
  originalPlanName: response.original_plan_name,
  showMultipleHotelPlans: response.show_multiple_hotel_plans,
  specialCheckinInstructions: response.special_checkin_instructions
});

export default class HotelOrderItemAgodaTodo {
  @observable readonly fromOrderItem: HotelOrderItemAgodaTodoMapperArgs;

  @observable readonly fromAgodaReservation: HotelOrderItemAgodaTodoMapperArgs;

  @observable readonly diff: { [key: string]: boolean };

  @observable readonly price: number | null;

  @observable readonly originalPrice: number | null;

  @observable readonly agodaPrice: number | null;

  @observable readonly agodaPriceUnconvertible: boolean;

  @observable readonly suppliedItemName: string | null;

  @observable readonly isInventoried: boolean;

  @observable readonly inventoriedBy: string | null;

  @observable readonly inventoriedAt: string | null;

  @observable readonly tripId: number | null;

  @observable readonly orderItemId: number | null;

  @observable readonly traceId: number | null;

  @observable readonly restricted: boolean | null;

  @observable readonly userChecker: boolean | null;

  @observable readonly lastLogMessage: string | null;

  @observable readonly lastLogMessageArranger: string | null;

  @observable readonly isDiff: boolean;

  @observable readonly originalPlanName: string | null;

  @observable readonly showMultipleHotelPlans: boolean;

  @observable readonly specialCheckinInstructions: string | null;

  constructor(args: HotelOrderItemAgodaTodoArgs) {
    this.fromOrderItem = args.fromOrderItem;
    this.fromAgodaReservation = args.fromAgodaReservation;
    this.diff = args.diff;
    this.price = args.price;
    this.originalPrice = args.originalPrice;
    this.agodaPrice = args.agodaPrice;
    this.agodaPriceUnconvertible = args.agodaPriceUnconvertible;
    this.suppliedItemName = args.suppliedItemName;
    this.isInventoried = args.isInventoried;
    this.inventoriedBy = args.inventoriedBy;
    this.inventoriedAt = args.inventoriedAt;
    this.tripId = args.tripId;
    this.orderItemId = args.orderItemId;
    this.traceId = args.traceId;
    this.restricted = args.restricted;
    this.userChecker = args.userChecker;
    this.lastLogMessage = args.lastLogMessage;
    this.lastLogMessageArranger = args.lastLogMessageArranger;
    this.isDiff = Object.values(this.diff).some(value => value);
    this.originalPlanName = args.originalPlanName;
    this.showMultipleHotelPlans = args.showMultipleHotelPlans;
    this.specialCheckinInstructions = args.specialCheckinInstructions;
  }

  isDiffPlanName(): boolean {
    return this.diff.plan_name;
  }

  userCheckerStr(): string | null {
    if (this.fromOrderItem.planName !== this.originalPlanName) {
      return null;
    }
    if (!this.restricted) {
      return null;
    }
    return this.userChecker ? 'チェック済み' : '未チェック';
  }

  static fromResponse(response: HotelOrderItemAgodaTodoResponseArgs) {
    return new HotelOrderItemAgodaTodo(convertHotelOrderItemAgodaTodoResponseToArgs(response));
  }
}
